// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/shortcuts/keys.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/shortcuts/src/keys.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo cartero-mail-app
//     node fleet/fleet.ts check --repo cartero-mail-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Key parsing & normalization.
 *
 * Canonical form: "Mod+Shift+K", "Alt+n", "?", "ArrowUp", "g i" (chord).
 * Modifier order is canonical: Mod, Ctrl, Alt, Shift (Mod = ⌘ on macOS, Ctrl elsewhere).
 *
 * Keys themselves are normalized to:
 *   - Lowercase single-character letters: "a".."z"
 *   - Digits as-is: "0".."9"
 *   - Named keys preserved (KeyboardEvent.key): "ArrowUp", "Enter", "Escape", " " (Space)
 *   - Special tokens: "Space" (canonicalised from " "), "?" stays "?", "/" stays "/"
 *
 * "Mod" is platform-aware: on macOS it means Meta (⌘); elsewhere Control.
 */

export interface KeySignature {
  mod: boolean; // platform meta-mod (Cmd on Mac, Ctrl elsewhere)
  ctrl: boolean; // explicit Control (independent of Mod)
  alt: boolean;
  shift: boolean;
  key: string; // canonical key token (always lowercased for letters)
}

const MOD_TOKEN_RE = /^(mod|cmd|meta|command|super)$/iu;
const CTRL_TOKEN_RE = /^(ctrl|control)$/iu;
const ALT_TOKEN_RE = /^(alt|option|opt)$/iu;
const SHIFT_TOKEN_RE = /^(shift)$/iu;

const SPACE_KEY = " ";

function normalizeKeyToken(raw: string): string {
  if (raw === SPACE_KEY || raw.toLowerCase() === "space") {
    return "Space";
  }
  if (raw.length === 1) {
    return raw.toLowerCase();
  }
  // Preserve canonical KeyboardEvent.key names (Enter, Escape, Tab, ArrowUp, …)
  return raw;
}

function parseKeySignature(spec: string): KeySignature {
  const tokens = spec.trim().split("+");
  const sig: KeySignature = { mod: false, ctrl: false, alt: false, shift: false, key: "" };
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i] ?? "";
    if (i < tokens.length - 1) {
      if (MOD_TOKEN_RE.test(tok)) {
        sig.mod = true;
        continue;
      }
      if (CTRL_TOKEN_RE.test(tok)) {
        sig.ctrl = true;
        continue;
      }
      if (ALT_TOKEN_RE.test(tok)) {
        sig.alt = true;
        continue;
      }
      if (SHIFT_TOKEN_RE.test(tok)) {
        sig.shift = true;
        continue;
      }
    }
    sig.key = normalizeKeyToken(tok);
  }
  return sig;
}

/** Splits a sequence string ("g i") into per-step signatures. */
export function parseKeySequence(spec: string): KeySignature[] {
  return spec
    .trim()
    .split(/\s+/u)
    .filter(Boolean)
    .map((step) => parseKeySignature(step));
}

/** True iff the given KeyboardEvent matches the signature on this platform. */
export function eventMatchesSignature(
  event: KeyboardEvent,
  sig: KeySignature,
  isMac: boolean,
): boolean {
  if (!isModSatisfied(event, sig, isMac)) {
    return false;
  }
  if (sig.alt !== event.altKey) {
    return false;
  }
  if (sig.shift !== event.shiftKey && !isShiftedPrintable(sig.key, event)) {
    return false;
  }
  return eventKeyEquals(event, sig.key);
}

function isModSatisfied(event: KeyboardEvent, sig: KeySignature, isMac: boolean): boolean {
  const metaPressed = event.metaKey;
  const ctrlPressed = event.ctrlKey;

  if (isMac) {
    // On Mac: mod = metaKey, ctrl = ctrlKey
    if (sig.mod && !metaPressed) {
      return false;
    }
    if (!sig.mod && metaPressed) {
      return false;
    }
    if (sig.ctrl && !ctrlPressed) {
      return false;
    }
    if (!sig.ctrl && ctrlPressed) {
      return false;
    }
    return true;
  }

  // Non-Mac: mod is satisfied by ctrlKey.
  // If both sig.mod and sig.ctrl are set, one ctrlKey is enough (they alias).
  const wantsCtrl = sig.mod || sig.ctrl;
  if (wantsCtrl && !ctrlPressed) {
    return false;
  }
  if (!wantsCtrl && ctrlPressed) {
    return false;
  }
  if (metaPressed) {
    return false;
  }
  return true;
}

/**
 * Some printable characters require Shift to type on standard layouts (e.g. "?"
 * or "/"). In that case `event.key` is already the shifted character, but the
 * author wrote "?" without `Shift+`. We tolerate `event.shiftKey === true` if the
 * signature asked for the shifted character directly.
 */
function isShiftedPrintable(targetKey: string, event: KeyboardEvent): boolean {
  if (targetKey.length !== 1) {
    return false;
  }
  return event.shiftKey && event.key === targetKey;
}

function eventKeyEquals(event: KeyboardEvent, expected: string): boolean {
  const actual = event.key;
  if (expected === "Space") {
    return actual === " " || actual === "Space" || actual === "Spacebar";
  }
  if (expected.length === 1) {
    return actual.toLowerCase() === expected.toLowerCase();
  }
  return actual === expected;
}

/** Serializes a key signature back to a canonical token for caching/equality. */
export function signatureKey(sig: KeySignature): string {
  const parts: string[] = [];
  if (sig.mod) {
    parts.push("Mod");
  }
  if (sig.ctrl) {
    parts.push("Ctrl");
  }
  if (sig.alt) {
    parts.push("Alt");
  }
  if (sig.shift) {
    parts.push("Shift");
  }
  parts.push(sig.key);
  return parts.join("+");
}

/** Builds a key signature from a KeyboardEvent for recording/rebinding. */
export function eventToSignature(event: KeyboardEvent, isMac: boolean): KeySignature {
  let mod = false;
  let ctrl = false;

  if (isMac) {
    mod = event.metaKey;
    ctrl = event.ctrlKey;
  } else {
    mod = event.ctrlKey;
    ctrl = false;
  }

  return {
    mod,
    ctrl,
    alt: event.altKey,
    shift: event.shiftKey,
    key: normalizeKeyToken(event.key),
  };
}
